import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { getIntelligenceGraph, type GraphResponse, type GraphNode, type GraphEdge } from '../lib/api';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse, shouldRetryApiError } from '../lib/api-errors';

const ANCHOR_TYPES = ['firm', 'facility', 'document', 'finding', 'inspection'] as const;
type AnchorType = (typeof ANCHOR_TYPES)[number];
type DepthValue = 1 | 2;

// ── SVG node/edge rendering (plain DOM/SVG — no graph library) ────────────────

interface SvgGraphProps {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

const NODE_COLORS: Record<string, string> = {
  firm: '#3b82f6',
  facility: '#8b5cf6',
  source_document: '#10b981',
  finding: '#f59e0b',
  inspection: '#ef4444',
  inspector: '#06b6d4',
  regulation: '#6b7280',
  gmp_system: '#84cc16',
  default: '#94a3b8',
};

function SvgGraph({ nodes, edges }: SvgGraphProps): React.ReactElement {
  if (nodes.length === 0) {
    return <p className="text-slate-400 text-sm text-center py-8">No nodes to display.</p>;
  }

  const W = 600;
  const H = 300;
  const RADIUS = 20;

  // Simple circular layout
  const nodePositions = new Map<string, { x: number; y: number }>();
  nodes.forEach((n, i) => {
    const angle = (i / nodes.length) * 2 * Math.PI;
    const rx = W / 2 - RADIUS - 10;
    const ry = H / 2 - RADIUS - 10;
    nodePositions.set(n.id, {
      x: W / 2 + rx * Math.cos(angle),
      y: H / 2 + ry * Math.sin(angle),
    });
  });

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full rounded border bg-slate-50"
      aria-label="Evidence graph visualization"
      role="img"
    >
      {/* Edges */}
      {edges.map((edge) => {
        const from = nodePositions.get(edge.from);
        const to = nodePositions.get(edge.to);
        if (!from || !to) return null;
        const isProbabilistic = edge.confidence < 1.0 && edge.confidence > 0;
        return (
          <g key={edge.edge_id}>
            <line
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={isProbabilistic ? '#fbbf24' : '#94a3b8'}
              strokeWidth={isProbabilistic ? 1 : 1.5}
              strokeDasharray={isProbabilistic ? '4 2' : undefined}
              opacity={0.7}
            />
            <text
              x={(from.x + to.x) / 2}
              y={(from.y + to.y) / 2 - 4}
              fontSize={8}
              fill="#64748b"
              textAnchor="middle"
            >
              {edge.type}
            </text>
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map((node) => {
        const pos = nodePositions.get(node.id);
        if (!pos) return null;
        const color = NODE_COLORS[node.type] ?? NODE_COLORS.default;
        return (
          <g key={node.id}>
            <circle cx={pos.x} cy={pos.y} r={RADIUS} fill={color} opacity={0.85} />
            <text
              x={pos.x}
              y={pos.y + RADIUS + 12}
              fontSize={9}
              fill="#334155"
              textAnchor="middle"
              className="select-none"
            >
              {node.label.slice(0, 20)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Node list grouped by type ─────────────────────────────────────────────────

interface NodeListProps {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

function NodeList({ nodes, edges }: NodeListProps): React.ReactElement {
  const byType = new Map<string, GraphNode[]>();
  for (const n of nodes) {
    const arr = byType.get(n.type) ?? [];
    arr.push(n);
    byType.set(n.type, arr);
  }

  return (
    <div className="space-y-4">
      {[...byType.entries()].map(([type, typeNodes]) => (
        <div key={type}>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            {type} ({typeNodes.length})
          </h4>
          <ul className="space-y-1">
            {typeNodes.map((n) => {
              const nodeEdges = edges.filter((e) => e.from === n.id || e.to === n.id);
              const hasProbabilistic = nodeEdges.some((e) => e.confidence < 1.0 && e.confidence > 0);
              return (
                <li key={n.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="font-medium">{n.label}</span>
                  {n.external_id && n.external_id !== n.label && (
                    <span className="text-xs text-slate-400 font-mono">{n.external_id}</span>
                  )}
                  {hasProbabilistic && (
                    <span
                      className="text-xs text-amber-600 ml-auto"
                      title="Some edges connecting this node are probabilistic inferences"
                    >
                      ⚠ probabilistic
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ── Graph Page ────────────────────────────────────────────────────────────────

export function IntelligenceGraphPage(): React.ReactElement {
  const { user, session } = useAuth();

  const [anchorType, setAnchorType] = useState<AnchorType>('document');
  const [anchorValue, setAnchorValue] = useState('');
  const [depth, setDepth] = useState<DepthValue>(1);
  const [submitted, setSubmitted] = useState(false);

  const cacheKey = user && session && submitted && anchorValue.trim()
    ? ['intelligence-graph', user.id, anchorType, anchorValue.trim(), depth]
    : null;

  const { data, error, isLoading } = useSWR<GraphResponse>(
    cacheKey,
    async () => {
      const res = await getIntelligenceGraph(session!.access_token, {
        anchor_type: anchorType,
        anchor_value: anchorValue.trim(),
        depth,
      });
      const body = (await res.json().catch(() => null)) as GraphResponse | { error: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Graph query failed.');
      }
      return body as GraphResponse;
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const graphData = data?.graph ?? null;
  const nodes: readonly GraphNode[] = graphData?.nodes ?? [];
  const edges: readonly GraphEdge[] = graphData?.edges ?? [];
  const warnings: readonly { code: string; message: string }[] = graphData?.warnings ?? [];

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (anchorValue.trim()) setSubmitted(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Evidence Graph</h2>
        <Link
          to="/intelligence/documents"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          ← Documents
        </Link>
      </div>

      <p className="text-sm text-slate-500 mb-5">
        Navigate evidence relationships between firms, facilities, documents, findings, and
        inspections. Depth 1–2 hops. Probabilistic edges are shown with warnings.
      </p>

      {/* Anchor picker */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="graph-anchor-type">
              Anchor type
            </label>
            <select
              id="graph-anchor-type"
              value={anchorType}
              onChange={(e) => { setAnchorType(e.target.value as AnchorType); setSubmitted(false); }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            >
              {ANCHOR_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="graph-anchor-value">
              Anchor value (ID or key)
            </label>
            <input
              id="graph-anchor-value"
              type="text"
              value={anchorValue}
              onChange={(e) => { setAnchorValue(e.target.value); setSubmitted(false); }}
              placeholder="e.g. UUID or FEI number"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="graph-depth">
              Depth
            </label>
            <select
              id="graph-depth"
              value={depth}
              onChange={(e) => { setDepth(parseInt(e.target.value, 10) as DepthValue); setSubmitted(false); }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
            >
              <option value={1}>1 hop</option>
              <option value={2}>2 hops</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={!anchorValue.trim() || !session}
            className="px-4 py-2 text-sm font-medium text-white bg-sentinel-700 rounded hover:bg-sentinel-800 disabled:opacity-50"
          >
            Explore Graph
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-6">
          <ApiErrorNotice error={error} fallbackMessage="Graph query failed." />
        </div>
      )}

      {isLoading && (
        <div className="text-slate-500 text-sm mb-6">Loading graph…</div>
      )}

      {/* Probabilistic warnings */}
      {warnings.filter((w) => w.code === 'PROBABILISTIC_LINK' || w.code === 'ANCHOR_NOT_FOUND').length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-6">
          <p className="text-sm font-semibold text-amber-800 mb-2">Graph warnings</p>
          <ul className="space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="text-sm text-amber-700">
                <span className="font-mono text-xs mr-1">[{w.code}]</span>
                {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {submitted && data && (
        <div className="space-y-6">
          {/* SVG visualization */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Graph — {nodes.length} node{nodes.length !== 1 ? 's' : ''},{' '}
              {edges.length} edge{edges.length !== 1 ? 's' : ''}
            </h3>
            <SvgGraph nodes={nodes} edges={edges} />
          </div>

          {/* Node list grouped by type */}
          {nodes.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Nodes by Type</h3>
              <NodeList nodes={nodes} edges={edges} />
            </div>
          )}

          {/* Edge list with confidence + evidence */}
          {edges.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Edges</h3>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">Type</th>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">From</th>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">To</th>
                    <th className="text-right px-3 py-2 text-slate-600 font-medium">Confidence</th>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {edges.map((edge, i) => {
                    const isProbabilistic = edge.confidence < 1.0 && edge.confidence > 0;
                    return (
                      <tr key={i} className={`border-t ${isProbabilistic ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{edge.type}</td>
                        <td className="px-3 py-2 text-slate-500 font-mono text-xs truncate max-w-[8rem]">{edge.from}</td>
                        <td className="px-3 py-2 text-slate-500 font-mono text-xs truncate max-w-[8rem]">{edge.to}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={isProbabilistic ? 'text-amber-700' : 'text-green-700'}>
                            {(edge.confidence * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{edge.match_basis}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {nodes.length === 0 && !isLoading && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-slate-400 text-sm">
              No graph data found for this anchor. The graph may not be populated yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
